import React, { useState, useRef, useEffect } from 'react';
import axios from 'axios';

const Finance = ({ students }) => {
    // --- SMART SEARCH & SELECTION STATE ---
    const [searchTerm, setSearchTerm] = useState('');
    const [isSearching, setIsSearching] = useState(false);
    const [selectedStudent, setSelectedStudent] = useState(null);
    const searchRef = useRef(null);

    // --- TRANSACTION FORM STATE ---
    const [feeCategory, setFeeCategory] = useState('Tuition Fee');
    const [amount, setAmount] = useState('');
    const [paymentMethod, setPaymentMethod] = useState('Cash');
    
    // --- LEDGER & PRINT STATE ---
    const [transactions, setTransactions] = useState([]);
    const [receiptToPrint, setReceiptToPrint] = useState(null);
    const [soaToPrint, setSoaToPrint] = useState(null);

    // Filter students for the search dropdown
    const filteredStudents = students?.filter(s => {
        const fullName = `${s.first_name} ${s.last_name}`.toLowerCase();
        const lrn = (s.lrn || '').toLowerCase();
        return fullName.includes(searchTerm.toLowerCase()) || lrn.includes(searchTerm.toLowerCase());
    }) || [];

    // --- 1. THE GET BRIDGE: Fetch History on Select ---
    const handleSelectStudent = async (student) => {
        try {
            const response = await axios.get(`/api/payments/${student.lrn}`);
            const fetchedHistory = response.data;

            let calculatedTotalPaid = 0;
            const formattedHistory = fetchedHistory.map(tx => {
                const isPOS = tx.remarks.includes('[POS Transaction]');
                if (!isPOS) {
                    calculatedTotalPaid += parseFloat(tx.amount);
                }
                return {
                    date: new Date(tx.payment_date).toLocaleString(),
                    amount: parseFloat(tx.amount),
                    remarks: tx.remarks
                };
            });

            const studentWithFinance = {
                ...student,
                assessment: student.assessment || 13100.00, 
                totalPaid: calculatedTotalPaid,
                history: formattedHistory
            };
            
            studentWithFinance.balance = studentWithFinance.assessment - studentWithFinance.totalPaid;

            setSelectedStudent(studentWithFinance);
            setSearchTerm(`${student.last_name}, ${student.first_name}`);
            setIsSearching(false);

        } catch (error) {
            console.error("Failed to fetch payment history:", error);
            // If server fails, load them anyway with 0 balance so we don't break the UI
            setSelectedStudent({
                 ...student, assessment: 13100.00, totalPaid: 0, balance: 13100.00, history: []
            });
            setSearchTerm(`${student.last_name}, ${student.first_name}`);
            setIsSearching(false);
        }
    };

    // Close dropdown on outside click
    useEffect(() => {
        const handleClickOutside = (event) => {
            if (searchRef.current && !searchRef.current.contains(event.target)) setIsSearching(false);
        };
        document.addEventListener("mousedown", handleClickOutside);
        return () => document.removeEventListener("mousedown", handleClickOutside);
    }, []);

    // --- 2. THE POST BRIDGE: Save New Payment ---
    const handleAddTransaction = async (e) => {
        e.preventDefault();
        if (!selectedStudent || !amount) return alert("Please select a student and enter an amount.");

        const parsedAmount = parseFloat(amount);
        const isAssessmentFee = ['Tuition Fee', 'Misc Fee', 'PTA Contribution'].includes(feeCategory);
        const combinedRemarks = `${feeCategory} (${paymentMethod})${!isAssessmentFee ? ' - [POS Transaction]' : ''} - Cashier 1`;

        const paymentPayload = {
            lrn: selectedStudent.lrn,
            amount: parsedAmount,
            payment_date: new Date().toISOString().slice(0, 19).replace('T', ' '),
            remarks: combinedRemarks
        };

        try {
            const response = await axios.post('/api/payments', paymentPayload);

            if (response.data.success) {
                const newTx = {
                    id: Date.now(),
                    date: new Date().toLocaleString(),
                    student: selectedStudent,
                    category: feeCategory,
                    amount: parsedAmount,
                    method: paymentMethod,
                    cashier: 'Cashier 1'
                };

                setTransactions([newTx, ...transactions]);
                
                const updatedStudent = {
                    ...selectedStudent,
                    totalPaid: isAssessmentFee ? selectedStudent.totalPaid + parsedAmount : selectedStudent.totalPaid,
                    balance: isAssessmentFee ? selectedStudent.balance - parsedAmount : selectedStudent.balance,
                    history: [{ date: newTx.date, amount: parsedAmount, remarks: combinedRemarks }, ...selectedStudent.history]
                };
                
                setSelectedStudent(updatedStudent);
                setAmount('');
                setFeeCategory('Tuition Fee');
            } else {
                alert("Server responded, but payment may not have saved.");
            }
        } catch (error) {
            console.error("Database Save Error:", error);
            alert("❌ Failed to save payment to the database.");
        }
    };

    // --- 3. PRINT FUNCTIONS ---
    const handlePrintReceipt = (tx) => {
        setSoaToPrint(null);
        setReceiptToPrint(tx);
        setTimeout(() => window.print(), 100);
    };

    const handlePrintSOA = () => {
        setReceiptToPrint(null);
        setSoaToPrint(selectedStudent);
        setTimeout(() => window.print(), 100);
    };

    const dailyTotal = transactions.reduce((sum, tx) => sum + tx.amount, 0);

    return (
        <div style={{ backgroundColor: 'white', padding: '30px', borderRadius: '15px', boxShadow: '0 4px 6px rgba(0,0,0,0.05)' }}>
            
            {/* --- CONTROL PANEL (HIDDEN ON PRINT) --- */}
            <div className="no-print">
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', borderBottom: '2px solid #e2e8f0', paddingBottom: '20px', marginBottom: '25px' }}>
                    <div>
                        <h2 style={{ color: '#10b981', margin: '0 0 5px 0' }}>💳 Cashier & Collections Engine</h2>
                        <p style={{ margin: 0, color: '#64748b', fontSize: '14px' }}>Process payments and generate Official Receipts.</p>
                    </div>
                    <div style={{ textAlign: 'right' }}>
                        <p style={{ margin: 0, fontSize: '12px', color: '#64748b', fontWeight: 'bold', textTransform: 'uppercase' }}>Today's Collection</p>
                        <h2 style={{ margin: 0, color: '#10b981', fontSize: '28px' }}>₱ {dailyTotal.toLocaleString('en-US', {minimumFractionDigits: 2})}</h2>
                    </div>
                </div>

                {/* SMART SEARCH */}
                <div style={{ position: 'relative', marginBottom: '25px' }} ref={searchRef}>
                    <label style={{ fontSize: '12px', fontWeight: 'bold' }}>Find Payee (Student)</label>
                    <input 
                        type="text" 
                        placeholder="Search by LRN or Name..." 
                        value={searchTerm}
                        onChange={(e) => {
                            setSearchTerm(e.target.value);
                            setIsSearching(true);
                            if(selectedStudent) setSelectedStudent(null);
                        }}
                        onFocus={() => setIsSearching(true)}
                        style={{ width: '100%', padding: '12px', borderRadius: '8px', border: '2px solid #cbd5e1', fontSize: '16px', fontWeight: 'bold', boxSizing: 'border-box' }}
                    />
                    {isSearching && searchTerm.length > 0 && (
                        <div style={{ position: 'absolute', top: '100%', left: 0, right: 0, backgroundColor: 'white', border: '1px solid #cbd5e1', borderRadius: '8px', marginTop: '5px', maxHeight: '250px', overflowY: 'auto', zIndex: 50, boxShadow: '0 10px 15px -3px rgba(0, 0, 0, 0.1)' }}>
                            {filteredStudents.map(s => (
                                <div key={s.lrn} onClick={() => handleSelectStudent(s)} style={{ padding: '15px', borderBottom: '1px solid #f1f5f9', cursor: 'pointer' }} onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#f8fafc'} onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'white'}>
                                    <div style={{ fontWeight: 'bold', color: '#1e293b' }}>{s.last_name}, {s.first_name}</div>
                                    <div style={{ fontSize: '12px', color: '#64748b' }}>LRN: {s.lrn} | {s.grade_level}</div>
                                </div>
                            ))}
                        </div>
                    )}
                </div>

                {/* ACTIVE STUDENT DASHBOARD */}
                {selectedStudent && (
                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '20px', marginBottom: '30px' }}>
                        
                        {/* Payment Form */}
                        <div style={{ backgroundColor: '#f8fafc', padding: '20px', borderRadius: '10px', border: '1px solid #e2e8f0' }}>
                            <h3 style={{ margin: '0 0 15px 0', fontSize: '16px', color: '#334155' }}>💰 Record Payment</h3>
                            <form onSubmit={handleAddTransaction} style={{ display: 'flex', flexDirection: 'column', gap: '15px' }}>
                                <div style={{ display: 'flex', gap: '10px' }}>
                                    <div style={{ flex: 1 }}>
                                        <label style={{ fontSize: '12px', fontWeight: 'bold' }}>Amount (₱)</label>
                                        <input type="number" step="0.01" required value={amount} onChange={(e) => setAmount(e.target.value)} style={{ width: '100%', padding: '10px', borderRadius: '6px', border: '1px solid #cbd5e1', boxSizing: 'border-box', fontWeight: 'bold' }} />
                                    </div>
                                    <div style={{ flex: 1 }}>
                                        <label style={{ fontSize: '12px', fontWeight: 'bold' }}>Category</label>
                                        <select value={feeCategory} onChange={(e) => setFeeCategory(e.target.value)} style={{ width: '100%', padding: '10px', borderRadius: '6px', border: '1px solid #cbd5e1', boxSizing: 'border-box' }}>
                                            <option value="Tuition Fee">Tuition Fee</option>
                                            <option value="Misc Fee">Misc Fee</option>
                                            <option value="PTA Contribution">PTA Contribution</option>
                                            <option value="Diploma Printing">Diploma Printing</option>
                                            <option value="Certificate Printing">Certificate Request</option>
                                            <option value="Other Fees">Other Fees</option>
                                        </select>
                                    </div>
                                </div>
                                <div style={{ display: 'flex', gap: '10px' }}>
                                     <div style={{ flex: 1 }}>
                                        <label style={{ fontSize: '12px', fontWeight: 'bold' }}>Payment Method</label>
                                        <select value={paymentMethod} onChange={(e) => setPaymentMethod(e.target.value)} style={{ width: '100%', padding: '10px', borderRadius: '6px', border: '1px solid #cbd5e1', boxSizing: 'border-box' }}>
                                            <option value="Cash">Cash</option>
                                            <option value="Bank Transfer">Bank Transfer</option>
                                            <option value="Check">Check</option>
                                        </select>
                                    </div>
                                </div>
                                <button type="submit" style={{ padding: '12px', backgroundColor: '#10b981', color: 'white', border: 'none', borderRadius: '6px', cursor: 'pointer', fontWeight: 'bold', fontSize: '16px', marginTop: '10px' }}>Save Payment</button>
                            </form>
                        </div>

                        {/* Financial Summary, SOA Print & History */}
                        <div style={{ backgroundColor: '#f0f9ff', padding: '20px', borderRadius: '10px', border: '1px solid #bae6fd', display: 'flex', flexDirection: 'column', justifyContent: 'flex-start' }}>
                            <div>
                                <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '10px', color: '#334155' }}>
                                    <span>Current Assessment:</span>
                                    <span>₱{selectedStudent.assessment?.toLocaleString('en-US', {minimumFractionDigits: 2}) || '0.00'}</span>
                                </div>
                                <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '15px', color: '#16a34a' }}>
                                    <span>Total Paid:</span>
                                    <span>- ₱{selectedStudent.totalPaid?.toLocaleString('en-US', {minimumFractionDigits: 2}) || '0.00'}</span>
                                </div>
                                <div style={{ display: 'flex', justifyContent: 'space-between', backgroundColor: 'white', padding: '15px', border: '2px solid #3b82f6', borderRadius: '8px', fontWeight: 'bold', fontSize: '18px' }}>
                                    <span>Balance:</span>
                                    <span style={{ color: '#ef4444' }}>₱{selectedStudent.balance?.toLocaleString('en-US', {minimumFractionDigits: 2}) || '0.00'}</span>
                                </div>
                                <button onClick={handlePrintSOA} style={{ width: '100%', padding: '12px', backgroundColor: '#3b82f6', color: 'white', border: 'none', borderRadius: '6px', cursor: 'pointer', fontWeight: 'bold', marginTop: '15px' }}>
                                    🖨️ Print Statement of Account (SOA)
                                </button>
                            </div>

                            {/* On-Screen Payment History Table */}
                            <div style={{ marginTop: '20px', borderTop: '2px solid #bae6fd', paddingTop: '15px' }}>
                                <h4 style={{ margin: '0 0 10px 0', color: '#334155', fontSize: '14px' }}>Recent Payments</h4>
                                <div style={{ maxHeight: '180px', overflowY: 'auto', backgroundColor: 'white', border: '1px solid #cbd5e1', borderRadius: '6px' }}>
                                    <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '12px', textAlign: 'left' }}>
                                        <thead style={{ position: 'sticky', top: 0, backgroundColor: '#f1f5f9' }}>
                                            <tr style={{ borderBottom: '1px solid #cbd5e1', color: '#64748b' }}>
                                                <th style={{ padding: '8px' }}>Date</th>
                                                <th style={{ padding: '8px' }}>Amount</th>
                                                <th style={{ padding: '8px' }}>Remarks</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {selectedStudent.history && selectedStudent.history.length > 0 ? (
                                                selectedStudent.history.map((hist, idx) => (
                                                    <tr key={idx} style={{ borderBottom: '1px solid #e2e8f0' }}>
                                                        <td style={{ padding: '8px' }}>{hist.date.split(',')[0]}</td>
                                                        <td style={{ padding: '8px', fontWeight: 'bold', color: '#16a34a' }}>₱{hist.amount.toLocaleString('en-US', {minimumFractionDigits: 2})}</td>
                                                        <td style={{ padding: '8px' }}>{hist.remarks}</td>
                                                    </tr>
                                                ))
                                            ) : (
                                                <tr><td colSpan="3" style={{ padding: '15px', textAlign: 'center', color: '#94a3b8' }}>No payments recorded.</td></tr>
                                            )}
                                        </tbody>
                                    </table>
                                </div>
                            </div>
                        </div>
                    </div>
                )}

                {/* DAILY LEDGER TABLE */}
                <h3 style={{ color: '#334155', fontSize: '16px', marginBottom: '15px', borderTop: '2px solid #f1f5f9', paddingTop: '20px' }}>Today's Cashier Ledger</h3>
                <div style={{ overflowX: 'auto', border: '1px solid #cbd5e1', borderRadius: '8px' }}>
                    <table style={{ width: '100%', borderCollapse: 'collapse', textAlign: 'left', fontSize: '14px', minWidth: '800px' }}>
                        <thead>
                            <tr style={{ backgroundColor: '#f1f5f9', borderBottom: '2px solid #cbd5e1' }}>
                                <th style={{ padding: '12px' }}>Ref ID</th>
                                <th style={{ padding: '12px' }}>Date</th>
                                <th style={{ padding: '12px' }}>Student Name</th>
                                <th style={{ padding: '12px', textAlign: 'right' }}>Amount</th>
                                <th style={{ padding: '12px', textAlign: 'center' }}>Action</th>
                            </tr>
                        </thead>
                        <tbody>
                            {transactions.length === 0 ? (
                                <tr><td colSpan="5" style={{ padding: '20px', textAlign: 'center', color: '#94a3b8' }}>No transactions recorded today.</td></tr>
                            ) : (
                                transactions.map((tx) => (
                                    <tr key={tx.id} style={{ borderBottom: '1px solid #f1f5f9' }}>
                                        <td style={{ padding: '12px', color: '#64748b' }}>TXN-{tx.id.toString().slice(-6)}</td>
                                        <td style={{ padding: '12px' }}>{tx.date}</td>
                                        <td style={{ padding: '12px', fontWeight: 'bold' }}>{tx.student.last_name}</td>
                                        <td style={{ padding: '12px', textAlign: 'right', fontWeight: 'bold', color: '#166534' }}>₱{tx.amount.toLocaleString('en-US', {minimumFractionDigits: 2})}</td>
                                        <td style={{ padding: '12px', textAlign: 'center' }}>
                                            <button onClick={() => handlePrintReceipt(tx)} style={{ padding: '6px 12px', backgroundColor: '#3b82f6', color: 'white', border: 'none', borderRadius: '4px', cursor: 'pointer', fontSize: '12px' }}>Print OR</button>
                                        </td>
                                    </tr>
                                ))
                            )}
                        </tbody>
                    </table>
                </div>
            </div>

            {/* --- NARROW RECEIPT TEMPLATE --- */}
            {receiptToPrint && (
                <div className="receipt-paper" style={{ display: 'none', width: '3.5in', padding: '0.2in', fontFamily: '"Courier New", Courier, monospace', fontSize: '12px', color: 'black' }}>
                    <div style={{ textAlign: 'center', marginBottom: '15px' }}>
                        <h3 style={{ margin: '0' }}>SAN HERMINIGILDO</h3>
                        <p style={{ margin: '0', fontSize: '10px' }}>P4 Alanib, Lantapan, Bukidnon</p>
                        <h2 style={{ margin: '0', borderTop: '1px dashed black', borderBottom: '1px dashed black', padding: '5px 0' }}>OFFICIAL RECEIPT</h2>
                    </div>
                    <p><strong>Date:</strong> {receiptToPrint.date}</p>
                    <p><strong>Name:</strong> {receiptToPrint.student.first_name} {receiptToPrint.student.last_name}</p>
                    <p><strong>Remarks:</strong> {receiptToPrint.category} ({receiptToPrint.method})</p>
                    <div style={{ display: 'flex', justifyContent: 'space-between', fontWeight: 'bold', borderTop: '1px dashed black', paddingTop: '10px' }}>
                        <span>TOTAL</span>
                        <span>₱ {receiptToPrint.amount.toLocaleString('en-US', {minimumFractionDigits: 2})}</span>
                    </div>
                </div>
            )}

            {/* --- FULL PAGE SOA TEMPLATE --- */}
            {soaToPrint && (
                <div className="soa-paper" style={{ display: 'none', padding: '40px', fontFamily: '"Times New Roman", Times, serif', backgroundColor: 'white' }}>
                    <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', gap: '20px', marginBottom: '40px', borderBottom: '2px solid black', paddingBottom: '20px' }}>
                        <img src="/owl.png" alt="SHAIS Logo" style={{ width: '80px' }} />
                        <div style={{ textAlign: 'center', color: 'black' }}>
                            <h2 style={{ margin: '0 0 5px 0', fontSize: '24px', textTransform: 'uppercase' }}>San Herminigildo Agro-Industrial School</h2>
                            <p style={{ margin: '0', fontSize: '14px' }}>P4 Alanib, Lantapan, 8722 Bukidnon</p>
                        </div>
                    </div>

                    <h1 style={{ textAlign: 'center', fontSize: '22px', textTransform: 'uppercase', marginBottom: '30px', letterSpacing: '2px' }}>Statement of Account</h1>

                    <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '30px', fontSize: '16px' }}>
                        <div>
                            <p style={{ margin: '5px 0' }}><strong>Student Name:</strong> {soaToPrint.last_name}, {soaToPrint.first_name}</p>
                            <p style={{ margin: '5px 0' }}><strong>LRN:</strong> {soaToPrint.lrn}</p>
                        </div>
                        <div style={{ textAlign: 'right' }}>
                            <p style={{ margin: '5px 0' }}><strong>Grade & Section:</strong> {soaToPrint.grade_level}</p>
                            <p style={{ margin: '5px 0' }}><strong>Date Printed:</strong> {new Date().toLocaleDateString()}</p>
                        </div>
                    </div>

                    <div style={{ border: '2px solid black', padding: '20px', marginBottom: '30px' }}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '10px', fontSize: '18px' }}>
                            <span>Total Assessment for School Year:</span>
                            <span>₱ {soaToPrint.assessment.toLocaleString('en-US', {minimumFractionDigits: 2})}</span>
                        </div>
                        <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '10px', fontSize: '18px', color: '#333' }}>
                            <span>Total Payments Made:</span>
                            <span>- ₱ {soaToPrint.totalPaid.toLocaleString('en-US', {minimumFractionDigits: 2})}</span>
                        </div>
                        <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: '10px', paddingTop: '10px', borderTop: '2px solid black', fontSize: '20px', fontWeight: 'bold' }}>
                            <span>CURRENT OUTSTANDING BALANCE:</span>
                            <span>₱ {soaToPrint.balance.toLocaleString('en-US', {minimumFractionDigits: 2})}</span>
                        </div>
                    </div>

                    <h3 style={{ fontSize: '16px', marginBottom: '10px' }}>Payment History</h3>
                    <table style={{ width: '100%', borderCollapse: 'collapse', textAlign: 'left', fontSize: '14px' }}>
                        <thead>
                            <tr style={{ borderBottom: '2px solid black' }}>
                                <th style={{ padding: '8px' }}>Date</th>
                                <th style={{ padding: '8px' }}>Remarks</th>
                                <th style={{ padding: '8px', textAlign: 'right' }}>Amount Paid</th>
                            </tr>
                        </thead>
                        <tbody>
                            {soaToPrint.history.length === 0 ? (
                                <tr><td colSpan="3" style={{ padding: '15px', textAlign: 'center' }}>No payments recorded yet.</td></tr>
                            ) : (
                                soaToPrint.history.map((hist, idx) => (
                                    <tr key={idx} style={{ borderBottom: '1px solid #ccc' }}>
                                        <td style={{ padding: '8px' }}>{hist.date.split(',')[0]}</td>
                                        <td style={{ padding: '8px' }}>{hist.remarks}</td>
                                        <td style={{ padding: '8px', textAlign: 'right' }}>₱ {hist.amount.toLocaleString('en-US', {minimumFractionDigits: 2})}</td>
                                    </tr>
                                ))
                            )}
                        </tbody>
                    </table>

                    <div style={{ marginTop: '80px', textAlign: 'right' }}>
                        <p style={{ margin: '0', fontWeight: 'bold', textDecoration: 'underline' }}>Cashier / Finance Office</p>
                        <p style={{ margin: '0', fontSize: '12px' }}>Authorized Signature</p>
                    </div>
                </div>
            )}
        </div>
    );
};

export default Finance;